import React, { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "@/components/ui/sonner";

const BarRow = ({ label, count, max, tone = "emerald" }) => {
    const pct = max > 0 ? Math.max(4, Math.round((count / max) * 100)) : 4;
    const tones = { emerald: "bg-emerald-500", sky: "bg-sky-500", violet: "bg-violet-500", amber: "bg-amber-500" };
    return (
        <div className="flex items-center gap-3 py-1.5">
            <div className="w-40 text-xs text-foreground truncate shrink-0" title={label}>{label}</div>
            <div className="flex-1 h-2.5 rounded-full bg-muted overflow-hidden">
                <div className={`h-full ${tones[tone]} rounded-full`} style={{ width: `${pct}%` }} />
            </div>
            <div className="w-10 text-right mono-score text-xs font-semibold text-foreground">{count}</div>
        </div>
    );
};

export const AnalyticsCard = ({ title, subtitle, rows, tone = "emerald", empty = "No data yet.", testId, valueKey = "count", labelKey = "value" }) => {
    const max = rows?.reduce((m, r) => Math.max(m, r[valueKey]), 0) || 0;
    return (
        <div className="card-soft p-5" data-testid={testId}>
            <div className="mb-3">
                <h3 className="font-heading font-semibold text-foreground">{title}</h3>
                {subtitle && <p className="text-xs text-muted-foreground">{subtitle}</p>}
            </div>
            {!rows || rows.length === 0 ? (
                <div className="text-xs text-muted-foreground py-6 text-center">{empty}</div>
            ) : (
                <div>
                    {rows.map((r, i) => (
                        <BarRow key={i} label={r[labelKey] || r.value || r.category || r.company || r.bucket || "—"} count={r[valueKey]} max={max} tone={tone} />
                    ))}
                </div>
            )}
        </div>
    );
};

export const StatPill = ({ label, value, tone = "emerald", sub, testId }) => {
    const tones = { emerald: "text-emerald-600", sky: "text-sky-600", violet: "text-violet-600", amber: "text-amber-600", rose: "text-rose-600" };
    return (
        <div className="card-soft p-4" data-testid={testId}>
            <div className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider">{label}</div>
            <div className={`mt-1 font-heading font-bold text-2xl ${tones[tone]}`}>{value}</div>
            {sub && <div className="text-xs text-muted-foreground mt-0.5">{sub}</div>}
        </div>
    );
};

export const AnalyticsPage = ({ endpoint, title, overline, render, testId }) => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    useEffect(() => {
        api.get(endpoint)
            .then((r) => setData(r.data))
            .catch((e) => toast.error(formatApiError(e)))
            .finally(() => setLoading(false));
    }, [endpoint]);

    return (
        <div data-testid={testId}>
            <div className="mb-6">
                <p className="overline">{overline}</p>
                <h1 data-testid="dash-page-title" className="mt-1 font-heading font-medium text-2xl sm:text-3xl tracking-tight text-foreground">
                    {title}
                </h1>
            </div>
            {loading ? (
                <div className="py-16 flex justify-center"><Loader2 size={18} className="animate-spin text-emerald-500" /></div>
            ) : !data ? (
                <div className="card-soft p-8 text-center text-muted-foreground">Unable to load.</div>
            ) : render(data)}
        </div>
    );
};
